import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { SuperAdminAuthProvider, useSuperAdminAuth } from "./contexts/SuperAdminAuthContext";
import { BusinessProvider } from "./contexts/BusinessContext";
import { CustomerProvider } from "./contexts/CustomerContext";
import { MarketplaceProvider } from "./contexts/MarketplaceContext";
import { CRMAuthProvider } from "./contexts/CRMAuthContext";
import WebRTCProvider from "./components/WebRTC/WebRTCProvider";
import { LanguageProvider } from "./contexts/LanguageContext";
import Login from "./components/Auth/Login";
import SignUp from "./components/Auth/SignUp";
import StaffSignUp from "./components/Auth/StaffSignUp";
import SignupSuccess from "./components/Auth/SignupSuccess";
import SignupCancel from "./components/Auth/SignupCancel";
import PrivacyPolicy from "./pages/Public/PrivacyPolicy";
import TermsAndConditions from "./pages/Public/TermsAndConditions";
import TrackingPage from "./pages/Public/TrackingPage";
import PricingPage from "./components/Pricing/PricingPage";
import Partners from "./pages/Public/Partners";
import PartnerVerify from "./pages/Public/PartnerVerify";
import BusinessDashboard from "./components/Business/BusinessDashboard";
import BusinessMfaBoundary from "./components/Business/mfa/BusinessMfaBoundary";
import SuperAdminLogin from "./pages/SuperAdmin/SuperAdminLogin";
import SuperAdminActivate from "./pages/SuperAdmin/Activate";
import VerifyOTP from "./pages/SuperAdmin/VerifyOTP";
import SuperAdminDashboard from "./pages/SuperAdmin/Dashboard";
import UnauthorizedAccess from "./pages/SuperAdmin/UnauthorizedAccess";
import { Toaster } from "./components/ui/toaster";
import ServiceStatusBanner from "./components/ServiceStatusBanner";

// Gate that prevents ALL child providers from mounting (and firing effects) until
// AuthContext has finished reading localStorage. Without this, CRMAuthProvider,
// BusinessProvider, and CustomerProvider mount immediately and fire API calls
// with no valid token, producing 401s on every page refresh.
const AuthLoadingGate = ({ children }) => {
  const { loading } = useAuth();
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-purple-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">Initializing...</p>
        </div>
      </div>
    );
  }
  return children;
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, isAuthenticated, loading } = useAuth();
  
  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold mb-2">Loading...</h2>
          <p className="text-gray-600">Please wait while we verify your authentication.</p>
        </div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Protected Route for SuperAdmin Portal
const SuperAdminProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useSuperAdminAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-br from-purple-900 to-purple-700">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold mb-2 text-white">Loading...</h2>
          <p className="text-purple-200">Verifying your super admin access...</p>
        </div>
      </div>
    );
  }

  return isAuthenticated ? children : <Navigate to="/superadmin-portal/login" replace />;
};

// Business / Customer / public plane shell — the FULL CRM provider stack (auth, tenant, marketplace, WebRTC,
// incoming-call/handoff UI). Mounted ONLY for non-SuperAdmin routes so the operator signaling socket and the
// Business handoff modal can never appear on the SuperAdmin control plane.
const BusinessShell = () => (
  <CRMAuthProvider>
    <BusinessProvider>
      <CustomerProvider>
        <MarketplaceProvider>
          <WebRTCProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<SignUp />} />
              <Route path="/signup/success" element={<SignupSuccess />} />
              <Route path="/signup/cancel" element={<SignupCancel />} />
              <Route path="/staff-signup" element={<StaffSignUp />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              {/* Public premium pricing page (no auth) — governed by GET /api/v1/public/pricing. */}
              <Route path="/pricing" element={<PricingPage />} />
              {/* Public Partner Program (no auth) — application intake + contact verification landing.
                  Distinct paths; do not shadow /login, /signup, /pricing, /privacy-policy or /business/*. */}
              <Route path="/partners" element={<Partners />} />
              <Route path="/partners/verify" element={<PartnerVerify />} />
              {/* CUSTOMER WEB = NONE. Servisfy's supported customer planes are the Android and
                  iOS Customer apps only; the supported web planes are SuperAdmin Web and Business
                  Web. This route mounted a discontinued Customer Web dashboard and, with the
                  signup option that fed it, was live in the deployed bundle. Removed — NOT
                  redirected: there is deliberately no replacement web destination for customers.
                  components/Customer/ is left on disk, NOT deleted: with this route and
                  /business/take-survey both gone it is transitively unreferenced (dead source),
                  and removing files requires a separate ownership/reference proof pass. */}
              <Route
                path="/business/*"
                element={
                  <ProtectedRoute allowedRoles={['admin', 'operator', 'manager', 'analyst', 'director', 'executive', 'it']}>
                    {/* DR-017 personal MFA boundary — wraps the whole subtree so a direct /business/* URL can't bypass it. */}
                    <BusinessMfaBoundary>
                      <BusinessDashboard />
                    </BusinessMfaBoundary>
                  </ProtectedRoute>
                }
              />
              <Route path="/" element={<Navigate to="/login" replace />} />
            </Routes>
            <Toaster />
          </WebRTCProvider>
        </MarketplaceProvider>
      </CustomerProvider>
    </BusinessProvider>
  </CRMAuthProvider>
);

// SuperAdmin control plane shell — ISOLATED. Only SuperAdminAuthProvider (OTP/JWT). It mounts NONE of the
// Business providers: no CRMAuthProvider (no cross-tab Firebase session lifecycle), no MarketplaceContext,
// no WebRTCProvider (no operator socket / no Business handoff modal), no tenant polling.
const SuperAdminShell = () => (
  <SuperAdminAuthProvider>
    <Routes>
      <Route path="/login" element={<SuperAdminLogin />} />
      <Route path="/activate" element={<SuperAdminActivate />} />
      <Route path="/verify-otp" element={<VerifyOTP />} />
      <Route path="/unauthorized" element={<UnauthorizedAccess />} />
      <Route path="/dashboard/*" element={
        <SuperAdminProtectedRoute>
          <SuperAdminDashboard />
        </SuperAdminProtectedRoute>
      } />
      <Route path="/" element={<Navigate to="/superadmin-portal/dashboard" replace />} />
    </Routes>
  </SuperAdminAuthProvider>
);

// Main App Routes — plane split at the ROUTE level. The two shells never coexist in one component tree.
const AppRoutes = () => {
  return (
    <AuthProvider>
      <AuthLoadingGate>
        <Routes>
          {/* Legacy SuperAdmin URL redirects — preserve bookmarks from before /superadmin-portal migration */}
          <Route path="/superadmin/login" element={<Navigate to="/superadmin-portal/login" replace />} />
          <Route path="/superadmin/verify-otp" element={<Navigate to="/superadmin-portal/verify-otp" replace />} />
          <Route path="/superadmin/unauthorized" element={<Navigate to="/superadmin-portal/unauthorized" replace />} />
          <Route path="/superadmin/dashboard/*" element={<Navigate to="/superadmin-portal/dashboard" replace />} />
          <Route path="/superadmin" element={<Navigate to="/superadmin-portal/login" replace />} />

          {/* SuperAdmin plane — isolated shell (no Business providers / WebRTC / handoff modal). */}
          <Route path="/superadmin-portal/*" element={<SuperAdminShell />} />

          {/* GOV-7 WS2-B — PUBLIC customer tracking. Rendered OUTSIDE BusinessShell: no CRMAuthProvider,
              no BusinessProvider, no WebRTCProvider, no login gate. Opaque token in the URL is the only input. */}
          <Route path="/track/:token" element={<TrackingPage />} />

          {/* Everything else — the Business/Customer/public plane with the full CRM + WebRTC stack. */}
          <Route path="/*" element={<BusinessShell />} />
        </Routes>
      </AuthLoadingGate>
    </AuthProvider>
  );
};

function App() {
  return (
    <LanguageProvider>
      <div className="App min-h-screen bg-gradient-to-br from-white via-green-50 to-purple-50">
        <ServiceStatusBanner />
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </div>
    </LanguageProvider>
  );
}

export default App;